import { Auth } from 'aws-amplify';
import dayjs from 'dayjs';

import { fileInfo } from '../graphql/FileInfo';
import {
  AimsFile,
  Deficiency,
  deficiencyAction,
  DeficiencyFileEdited,
  InspectionFilesEdited,
  ISignAndStampFileBulk,
} from '../models/models';
import type { DeficiencyActionedList } from '../rxdb/deficiencyActionedList/deficiencyActionedList.types';
import type { DeficiencyComponentRemoval } from '../rxdb/deficiencyComponentRemoval/deficiencyComponentRemoval.types';
import type { DeficiencyFiles } from '../rxdb/deficiencyFiles/deficiencyFiles.types';
import { RxdbCollectionName } from '../rxdb/rxdbCollectionName';
import type { TaskList } from '../rxdb/taskList/taskList.types';
import { isJestTest } from '../test-helpers/isJestTest';
import {
  ADHOC_FI_SIGN_DONE_SUBMIT_FOR_LI_SIGN_OFF,
  ADHOC_LI_FI_SIGN_DONE_SUBMIT_TO_SC,
  ADHOC_SUBMIT_FOR_FI_SIGN_OFF,
  ADHOC_SUBMIT_FOR_LI_SIGN_OFF,
  COMPLETING_INSPECTION_FROM_INPROGRESS,
  DEC_SUBMIT_TO_LI,
  DOC,
  DOCX,
  FI_SIGN_DONE_SUBMIT_FOR_LI_REVIEW,
  INSP_BY_DEC_SUBMIT_FOR_LI_SIGN_OFF,
  INSPECTION_ASSIGN_BACK_TO_DEC,
  INSPECTION_ASSIGN_BACK_TO_FI,
  JPEG,
  LI_SIGN_DONE_SUBMIT_TO_SC,
  MOV,
  MP3,
  MP4,
  NOTIFICATION_ASSIGN_BACK,
  NOTIFICATION_ASSIGN_BACK_DEC,
  NOTIFICATION_FI_SIGN_OFF,
  NOTIFICATION_LI_SIGN_OFF,
  NOTIFICATION_SC_REVIEW,
  PDF,
  PNG,
  SUBMIT_FOR_FI_SIGN_STAMP,
  SUBMIT_FOR_LI_REVIEW,
  TEXT,
  TITLE_COMPLETED,
  TITLE_REVIEW,
  TITLE_SIGN_OFF,
  XLS,
  XLSX,
  YYYYMMDD_DATE_FORMAT,
} from '../utilities/constants';
import {
  AreaType,
  FileCategory,
  FileType,
  Filter,
  InspectionStatus,
  InspectorRole,
  RiskAssessment,
  TaskStatus,
} from './enums';
import { sentryCaptureException } from './sentryCaptureException';
import { getImage } from './storageUtils';

export const getDateModified = (
  dateModified: string | undefined | null,
  dateCreated: string | undefined | null,
  oldSiteId: number | undefined,
  oldDisciplineId: number | undefined,
  newSiteId: number,
  newDisciplineId: number,
  area = false,
) => {
  if (area) {
    localStorage.removeItem('oldSiteId');
    localStorage.removeItem('oldDisciplineId');
  }
  const localDateModified = '1900-01-01T01:01:01.1234567-06:00';

  if (!oldSiteId || !oldDisciplineId) {
    return dateModified ? dateModified : dateCreated;
  } else {
    const isDataAvailable =
      oldSiteId &&
      oldDisciplineId &&
      (oldSiteId !== newSiteId || oldDisciplineId !== newDisciplineId);

    if (isDataAvailable) return localDateModified;
    else return dateModified ? dateModified : dateCreated;
  }
};

export const masterDataArray = [
  RxdbCollectionName.REMEDIAL_ACTION,
  RxdbCollectionName.PRIMARY_CAUSE_OF_FAILURE,
  RxdbCollectionName.CONDITION_RATING,
  RxdbCollectionName.OPERATIONAL_DOWNTIME,
  RxdbCollectionName.TRAFFIC_SAFETY_SCORE,
  RxdbCollectionName.URGENCY,
  RxdbCollectionName.REPAIR_FACTOR,
  RxdbCollectionName.COMPONENT_REMOVAL_CATEGORIES,
];

export const getValueFromLocalStorage = (key: string): string => {
  return localStorage.getItem(key) || '';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setValueinLocalStorage = (key: string, value: any) => {
  localStorage.setItem(key, value);
};

export const isLiAndFiAreSame = () => {
  return (
    Number(getValueFromLocalStorage('taskLeadInspectorId')) ===
      Number(getValueFromLocalStorage('taskFieldInspectorId')) &&
    Number(getValueFromLocalStorage('taskStatus')) === TaskStatus.LI_SIGN_PENDING &&
    Number(getValueFromLocalStorage('inspectionStatus')) === InspectionStatus.LI_SIGN_PENDING
  );
};

export const isTaskAssignedtoFieldInspector = (): boolean => {
  return (
    (Number(getValueFromLocalStorage('taskFieldInspectorId')) ===
      Number(getValueFromLocalStorage('inspectorId')) &&
      [2, 5, 8].includes(Number(getValueFromLocalStorage('taskStatus'))) &&
      [2, 5, 9].includes(Number(getValueFromLocalStorage('inspectionStatus')))) ||
    isLiAndFiAreSame()
  );
};

export const getStatus = (id: number): string => {
  switch (id) {
    case 1:
      return 'Upcoming';
    case 2:
      return 'In Progress';
    case 3:
      return 'Pending';
    case 4:
      return 'Completed';
    case 5:
      return 'Assigned';
    case 6:
      return 'LI Pending Approval';
    case 7:
      return 'SC Pending Approval';
    case 8:
      return 'FI Sign Pending';
    case 9:
      return 'LI Sign Pending';
    default:
      return '';
  }
};

export const getStatusForInspection = (id: number): string => {
  switch (id) {
    case 1:
      return 'Unassigned';
    case 2:
      return 'In Progress';
    case 3:
      return 'Pending';
    case 4:
      return 'Completed';
    case 5:
      return 'Assigned';
    case 7:
      return 'LI Pending Approval';
    case 8:
      return 'SC Pending Approval';
    case 9:
      return 'FI Sign Pending';
    case 10:
      return 'LI Sign Pending';
    default:
      return '';
  }
};

export const getStatusForDeficiency = (statusId: number, workPackageId: number): string => {
  switch (true) {
    case statusId === 4:
      return 'Completed';
    case statusId === 2:
      return 'In progress';
    case workPackageId && (statusId === 1 || statusId === 3):
      return 'Scheduled';
    case !workPackageId && (statusId === 1 || statusId === 3):
      return 'Outstanding';
    default:
      return '';
  }
};

export const getStatusColorForDeficiency = (statusId: number, workPackageId: number): string => {
  switch (true) {
    case statusId === 4:
      return 'rgba(38, 128, 61, 1)'; // Completed
    case statusId === 2:
      return 'rgb(42, 171, 63)'; // In Progress
    case workPackageId > 0 && (statusId === 1 || statusId === 3):
      return 'rgb(42, 171, 63)'; // Scheduled
    case workPackageId === null && (statusId === 1 || statusId === 3):
      return 'rgb(220, 119, 135)'; // Outstanding
    default:
      return '';
  }
};

export const getStatusColor = (id: number): string => {
  switch (id) {
    case 1:
      return 'rgb(39,126,126)'; // Upcoming;
    case 2:
      return 'rgb(42,171,63)'; // In Progress;
    case 3:
      return 'rgb(39,126,126)'; // Pending;
    case 4:
      return 'rgba(38, 128, 61, 1)'; // Completed;
    case 5:
      return 'rgb(39,126,126)'; // Assigned;
    case 6:
      return 'rgb(220, 119, 135)'; // LI Pending Approval;
    case 7:
      return 'rgb(220, 119, 135)'; // SC Pending Approval;
    case 8:
      return 'rgba(33, 110, 165, 1)'; // FI Stamping Pending;
    case 9:
      return 'rgba(33, 110, 165, 1)'; // LI Stamping Pending;
    default:
      return '';
  }
};

export const getStatusColorForInspection = (id: number): string => {
  switch (id) {
    case 1:
      return 'rgb(39,126,126)'; // Upcoming;
    case 2:
      return 'rgb(42,171,63)'; // In Progress;
    case 3:
      return 'rgb(39,126,126)'; // Pending";
    case 4:
      return 'rgba(38, 128, 61, 1)'; // Completed;
    case 5:
      return 'rgb(39,126,126)'; // Assigned;
    case 7:
      return 'rgb(220, 119, 135)'; // LI Pending Approval;
    case 8:
      return 'rgb(220, 119, 135)'; // SC Pending Approval;
    case 9:
      return 'rgba(33, 110, 165, 1)'; // FI Stamping Pending;
    case 10:
      return 'rgba(33, 110, 165, 1)'; // LI Stamping Pending;
    default:
      return '';
  }
};

export const statusCheck = (statusId: number, tabNumber: number): boolean => {
  switch (tabNumber) {
    case 1:
      return statusId === 1;
    case 2:
      return [2, 3, 5].includes(statusId);
    case 3:
      return [6, 7, 8, 9, 10].includes(statusId);
    default:
      return false;
  }
};

export const getRole = (id: number): string => {
  switch (id) {
    case 3:
      return 'Site Co-ordinator';
    case 6:
      return 'Lead Inspector';
    case 7:
      return 'Field Inspector';
    default:
      return '';
  }
};

export const checkTaskStatusForReAssign = (statusId: number): boolean =>
  [
    TaskStatus.IN_PROGRESS,
    TaskStatus.ASSIGNED,
    TaskStatus.LI_PENDING_APPROVAL,
    TaskStatus.SC_PENDING_APPROVAL,
    TaskStatus.FI_SIGN_PENDING,
    TaskStatus.LI_SIGN_PENDING,
  ].includes(statusId);

export const checkInspectionStatusForUpdate = (id: number): boolean => {
  return [
    InspectionStatus.IN_PROGRESS,
    InspectionStatus.ASSIGNED,
    InspectionStatus.LI_PENDING_APPROVAL,
    InspectionStatus.SC_PENDING_APPROVAL,
    InspectionStatus.FI_SIGN_PENDING,
  ].includes(id);
};

export const downloadURI = (uri: string, name: string) => {
  const link = document.createElement('a');
  link.download = name;
  link.href = uri;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export interface FileData {
  fileUUId: string;
  dateModified: string;
  isUploadedByUser: boolean;
}

export const setDeltaFileData = (deltaFiles: FileData[]) => {
  try {
    const oldFiles: FileData[] = JSON.parse(localStorage.getItem('filesData') || '[{}]')
      ? JSON.parse(localStorage.getItem('filesData') || '[{}]')
      : [];
    const newDeltaFiles: FileData[] = [];

    deltaFiles.forEach((e: FileData, index: number) => {
      const indexValue = oldFiles.findIndex(v => e.fileUUId === v.fileUUId);
      if (indexValue === -1) {
        newDeltaFiles.push(deltaFiles[index]);
      } else {
        oldFiles[indexValue].dateModified = deltaFiles[index].dateModified;
      }
    });

    localStorage.setItem('filesData', JSON.stringify([...oldFiles, ...newDeltaFiles]));

    return;
  } catch (error) {
    console.log('Set delta files error ', error); // eslint-disable-line no-console
    // console logs will be removed once sentry implementation is done
    sentryCaptureException(error, '~ file: UtilityFunctions.tsx ~ line 55 ~ setDeltaFileData ~');
    return;
  }
};

export const checkIfExist = (item: { fileUUID: string; dateModified: string | undefined }) => {
  try {
    return JSON.parse(localStorage.getItem('filesData') || '[{}]')?.find(
      (e: FileData) =>
        e.fileUUId === item.fileUUID &&
        (e.dateModified === item.dateModified || e.isUploadedByUser),
    );
  } catch (error) {
    console.log('check file exist error ', error); // eslint-disable-line no-console
    // console logs will be removed once sentry implementation is done
    sentryCaptureException(error, '~ file: UtilityFunctions.tsx ~ line 55 ~ checkIfExist ~');
    return;
  }
};

export const groupArrayByKey = (
  array: DeficiencyFiles[] | AimsFile[] | InspectionFilesEdited[],
  key: string,
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (array as any[]).reduce((res: any, val: any) => {
    const result = res;
    (result[val[key]] = result[val[key]] || []).push(val);
    return result;
  }, {});
};

export const groupObjectArrayByKey = (array: DeficiencyFiles[] | AimsFile[], key: string) => {
  if (array && key) {
    /* eslint-disable */
    return Object.keys(array).reduce((res: any, val: any) => {
      const result = res;
      (result[(array as any[])[val][key]] = result[(array as any[])[val][key]] || []).push(
        (array as any[])[val],
      );
      return result;
    }, {});
    /* eslint-enable */
  }
  return {};
};

export interface InspectorData {
  fieldInspectorId: number;
  fieldInspectorName: string;
  inspectionId: number;
  isActive: boolean;
}
export const getLocalStorageSelectedSiteId = (): string => {
  const selectedSite = localStorage.getItem('selectedSite');
  const selectedSiteId = selectedSite !== null ? JSON.parse(selectedSite)?.selectedSiteId : '';
  return selectedSiteId;
};

export const getLocalStorageSelectedDisciplineId = (): string => {
  const selectedDiscipline = localStorage.getItem('selectedDiscipline');
  const selectedDisciplineId =
    selectedDiscipline !== null ? JSON.parse(selectedDiscipline)?.selectedDisciplineId : '';
  return selectedDisciplineId;
};

export const getLocalStorageCurrentUserId = (): number => {
  const currentUser = localStorage.getItem('currentUser');
  const userId = currentUser !== null ? JSON.parse(currentUser)?.userId : 0;
  return userId;
};

export const getAreaType = (isShaft: boolean, areaType: number) => {
  switch (areaType) {
    case 1:
    case 4:
      return AreaType.MainArea;
    case 2:
    case 5:
      return AreaType.SubArea;
    case 3:
    case 6:
      return isShaft ? AreaType.Elevation : AreaType.CubeArea;
    default:
      return AreaType.Asset;
  }
};

export const getFileDataBasedOnType = (extension: string) => {
  switch (extension) {
    case 'pdf':
      return { src: '../../assets/images/pdf128_128.svg', alt: 'pdf' };
    case 'mp4':
      return { src: '../../assets/images/mp4128_128.svg', alt: 'mp4' };
    case 'mp3':
      return { src: '../../assets/images/mp3128_128.svg', alt: 'mp3' };
    case 'txt':
      return { src: '../../assets/images/txt128_128.svg', alt: 'txt' };
    case 'csv':
      return { src: '../../assets/images/csv128_128.svg', alt: 'csv' };
    case 'xlsx':
    case 'xls':
      return { src: '../../assets/images/xls128_128.svg', alt: 'xls' };
    case 'ppt':
    case 'pptx':
      return { src: '../../assets/images/ppt128_128.svg', alt: 'ppt' };
    case 'docx':
    case 'doc':
      return { src: '../../assets/images/doc128_128.svg', alt: 'doc' };
    case 'mov':
      return { src: '../../assets/images/mov.svg', alt: 'mov' };
    default:
      return { src: '', alt: '' };
  }
};

export const autoVersionIfFileExists = (
  fileName: string,
  listOffiles: AimsFile[],
  index = 0,
): string => {
  let newName = fileName,
    ext = '';
  if (index) {
    if (newName.indexOf('.') > -1) {
      const tokens = newName.split('.');
      ext = '.' + tokens.pop();
      newName = tokens.join('.');
    }
    newName = `${newName} (${index})${ext}`;
  }
  const nameExists = listOffiles.filter((f: AimsFile) => f.updatedName === newName).length > 0;
  return nameExists ? autoVersionIfFileExists(fileName, listOffiles, index + 1) : newName;
};

export const getFileExtension = (fileName: string) => {
  return /[.]/.exec(fileName) ? /[^.]+$/.exec(fileName) : undefined;
};

export const getFileOrder = (list: DeficiencyFileEdited[] | InspectionFilesEdited[]) => {
  const fileOrder = list[list.length - 1]?.fileSortOrder;
  if (fileOrder !== undefined) {
    return fileOrder + 1;
  } else {
    return list.length;
  }
};

export const getContentType = (ext: string) => {
  switch (ext) {
    case '.pdf':
      return PDF;
    case '.doc':
      return DOC;
    case '.docx':
      return DOCX;
    case '.mp4':
      return MP4;
    case '.mov':
      return MOV;
    case '.mp3':
      return MP3;
    case '.txt':
      return TEXT;
    case '.csv':
    case '.xls':
      return XLS;
    case '.xlsx':
      return XLSX;
    case '.png':
      return PNG;
    case '.jpg':
    case '.jpeg':
      return JPEG;
    default:
      throw new Error(`${ext}`);
  }
};

export const getDateTimeFormat = (dateTime: string) => {
  const dateUTC = new Date(dateTime);
  const dateUTCNum = dateUTC.getTime();
  const dateIST = new Date(dateUTCNum);
  dateIST.setHours(dateIST.getHours() + 6);
  return dayjs(dateIST).format(
    `\u00A0\u00A0\u00A0\u00A0${YYYYMMDD_DATE_FORMAT}\u00A0\u00A0\u00A0\u00A0HH:mm:ss`,
  );
};

export const getDisciplineById = (disciplineId: number) => {
  switch (disciplineId) {
    case 1:
      return 'Electrical';
    case 2:
      return 'Mechanical';
    case 3:
      return 'Structural';
    case 4:
      return 'Shaft';
    default:
      return '';
  }
};

interface MutationCollectionNames {
  [key: string]: string;
  task_list: string;
  deficiency_list: string;
  deficiency_actioned_list: string;
  risk_assessment: string;
  deficiency_files: string;
  notification: string;
  sign_and_stamp_file: string;
  inspection_files: string;
  save_pmchecklist: string;
  component_checklist: string;
  sign_and_stamp_user_level: string;
  planned_deficiency_files: string;
}

export const getDocumentIdByCollectionName = (collectionName: string) => {
  const primaryKeyIds: MutationCollectionNames = {
    [RxdbCollectionName.TASK_LIST]: 'pkUuId',
    [RxdbCollectionName.DEFICIENCY_LIST]: 'pkDeficiencyUUID',
    [RxdbCollectionName.RISK_ASSESSMENT]: 'pkComponentRiskUUID',
    [RxdbCollectionName.DEFICIENCY_FILES]: 'fileAimsUUID',
    [RxdbCollectionName.NOTIFICATION]: 'pkUSerAlertId',
    [RxdbCollectionName.SIGN_AND_STAMP_FILE]: 'fileUUID',
    [RxdbCollectionName.INSPECTION_FILES]: 'fileUUID',
    [RxdbCollectionName.SAVE_PMCHECKLIST]: 'pk_uuid',
    [RxdbCollectionName.COMPONENT_CHECKLIST]: 'pk_uuid',
    [RxdbCollectionName.SIGN_AND_STAMP_USER_LEVEL]: 'fileUUID',
    [RxdbCollectionName.PLANNED_DEFICIENCY_FILES]: 'fileAimsUUID',
  };
  return primaryKeyIds[collectionName];
};

export const getRequestUrlByCollectionName = (collectionName: string) => {
  const primaryKeyIds: MutationCollectionNames = {
    [RxdbCollectionName.TASK_LIST]: 'mobileFIAddOrUpdateInspection',
    [RxdbCollectionName.DEFICIENCY_LIST]: 'mobileAddOrUpdateDeficiency',
    [RxdbCollectionName.RISK_ASSESSMENT]: 'mobileAddOrUpdateComponentRiskResult',
    [RxdbCollectionName.DEFICIENCY_FILES]: 'cEDDeficiencyFileInfo',
    [RxdbCollectionName.NOTIFICATION]: 'mobileMarkAlertAsRead',
    [RxdbCollectionName.SIGN_AND_STAMP_FILE]: 'userSignAndStampFileInfo',
    [RxdbCollectionName.INSPECTION_FILES]: 'cEDInspectionFileInfo',
    [RxdbCollectionName.SAVE_PMCHECKLIST]: 'saveUpdateMobileAssetInspectionPMChecklist',
    [RxdbCollectionName.COMPONENT_CHECKLIST]: 'saveDeleteComponentCheckList',
    [RxdbCollectionName.SIGN_AND_STAMP_USER_LEVEL]: 'userSignAndStampFileInfo',
    [RxdbCollectionName.PLANNED_DEFICIENCY_FILES]: 'cEDDeficiencyFileInfo',
  };
  return primaryKeyIds[collectionName];
};

export const getRiskType = (name: string) => {
  switch (name) {
    case RiskAssessment.OPERATIONAL_DOWNTIME:
      return 1;
    case RiskAssessment.TRAFFIC_SAFETY_SCORE:
      return 3;
    case RiskAssessment.RATING:
      return 8;
    case RiskAssessment.URGENCY:
      return 9;
    default:
      return 0;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNull = (data: any, property: string, defaultReturn: any) => {
  return data && data[property] ? data[property] : defaultReturn;
};

export const downloadFile = async (fileId: number) => {
  try {
    if (isJestTest()) return;
    const result = await fileInfo(fileId);
    if (result && result.data.fileInfo) {
      const URL = await getImage(result.data.fileInfo);
      window.open(URL.toString(), '_blank');
    }
  } catch (error) {
    console.error('Failed to load image');
    sentryCaptureException(error, 'Failed to load image');
  }
};

export const checkIfTokenExpired = async () => {
  const currentTimestamp = dayjs().unix();
  let tokenExpired = false;
  let currentIdTokenExpiration = -1;
  const currentSession = await Auth.currentSession();
  currentIdTokenExpiration = currentSession.getIdToken().getExpiration(); // seconds
  const diff = currentIdTokenExpiration - currentTimestamp; // if this is positive its still a valid token
  tokenExpired = diff <= 0;
  return tokenExpired || localStorage.getItem('TEST') === 'true'; //Adding debugger for testing purpose
};

export const disciplineMapping = (disciplineId: number) => {
  switch (disciplineId) {
    case 1:
      return 'Electrical';
    case 2:
      return 'Mechanical';
    case 3:
      return 'Structural';
    case 4:
      return 'Shaft';
    default:
      return '';
  }
};

export const getStatusAndMessageOnSubmitFromUtility = (
  isFiScSame: boolean,
  isLiFiSame: boolean,
  isAdhoc: boolean,
  isFISignAvailable: boolean,
  isLISignAvailable: boolean,
  isDECUser: boolean,
) => {
  if (isLiFiSame) {
    if (isAdhoc) {
      if (isFiScSame) {
        if (isLISignAvailable)
          return {
            status: TaskStatus.COMPLETED,
            message: COMPLETING_INSPECTION_FROM_INPROGRESS,
            title: TITLE_COMPLETED,
          };
        return {
          status: TaskStatus.LI_SIGN_PENDING,
          message: ADHOC_SUBMIT_FOR_LI_SIGN_OFF,
          title: TITLE_SIGN_OFF,
          isLiSignEnabled: true,
        };
      }
      if (isLISignAvailable)
        return {
          status: TaskStatus.SC_PENDING_APPROVAL,
          message: LI_SIGN_DONE_SUBMIT_TO_SC,
          title: TITLE_REVIEW,
        };
      return {
        status: TaskStatus.LI_SIGN_PENDING,
        message: ADHOC_SUBMIT_FOR_LI_SIGN_OFF,
        title: TITLE_SIGN_OFF,
        isLiSignEnabled: true,
      };
    }
    if (!isFISignAvailable) {
      if (isLISignAvailable) {
        // if Li and Fi are Same and Already Li signed then move status to sc pending parroval
        return {
          status: TaskStatus.SC_PENDING_APPROVAL,
          message: LI_SIGN_DONE_SUBMIT_TO_SC,
          title: TITLE_REVIEW,
        };
      }
      // else move status to li sign pending
      return {
        status: TaskStatus.LI_SIGN_PENDING,
        message: ADHOC_SUBMIT_FOR_LI_SIGN_OFF,
        title: TITLE_SIGN_OFF,
      };
    }
    if (isFISignAvailable) {
      if (isLISignAvailable) {
        return {
          status: TaskStatus.SC_PENDING_APPROVAL,
          message: LI_SIGN_DONE_SUBMIT_TO_SC,
          title: TITLE_REVIEW,
        };
      }
      return {
        status: TaskStatus.LI_SIGN_PENDING,
        message: ADHOC_SUBMIT_FOR_LI_SIGN_OFF,
        title: TITLE_SIGN_OFF,
      };
    }
  }
  if (isDECUser) {
    return {
      status: TaskStatus.LI_PENDING_APPROVAL,
      message: DEC_SUBMIT_TO_LI,
      title: TITLE_REVIEW,
    };
  }
  if (isAdhoc) {
    return {
      status: TaskStatus.LI_PENDING_APPROVAL,
      message: SUBMIT_FOR_LI_REVIEW,
      title: TITLE_REVIEW,
    };
  } else {
    if (isFISignAvailable) {
      return {
        status: TaskStatus.LI_PENDING_APPROVAL,
        message: FI_SIGN_DONE_SUBMIT_FOR_LI_REVIEW,
        title: TITLE_REVIEW,
      };
    }
    return {
      status: TaskStatus.FI_SIGN_PENDING,
      message: SUBMIT_FOR_FI_SIGN_STAMP,
      title: TITLE_SIGN_OFF,
    };
  }
};

export const getStatusAndMessageOnReviewFromUtility = (
  isSubmitToSC: boolean,
  isLiFiSame: boolean,
  isAdhoc: boolean,
  isFISignAvailable: boolean,
  isLISignAvailable: boolean,
  assignedByDEC: boolean,
) => {
  if (!isSubmitToSC) {
    if (assignedByDEC) {
      return {
        status: TaskStatus.IN_PROGRESS,
        message: INSPECTION_ASSIGN_BACK_TO_DEC,
        title: 'Submit Inspection',
        notification: NOTIFICATION_ASSIGN_BACK_DEC,
      };
    }
    return {
      status: TaskStatus.IN_PROGRESS,
      message: INSPECTION_ASSIGN_BACK_TO_FI,
      title: 'Submit Inspection',
      notification: NOTIFICATION_ASSIGN_BACK,
    };
  }

  if (isAdhoc) {
    if (isLiFiSame) {
      if (isLISignAvailable)
        return {
          status: TaskStatus.SC_PENDING_APPROVAL,
          message: LI_SIGN_DONE_SUBMIT_TO_SC,
          title: TITLE_REVIEW,
          notification: NOTIFICATION_SC_REVIEW,
        };
      return {
        status: TaskStatus.LI_SIGN_PENDING,
        message: ADHOC_SUBMIT_FOR_LI_SIGN_OFF,
        title: TITLE_SIGN_OFF,
        isLiSignEnabled: true,
        notification: NOTIFICATION_LI_SIGN_OFF,
      };
    } else {
      if (isFISignAvailable) {
        if (isLISignAvailable)
          return {
            status: TaskStatus.SC_PENDING_APPROVAL,
            message: ADHOC_LI_FI_SIGN_DONE_SUBMIT_TO_SC,
            title: TITLE_REVIEW,
            notification: NOTIFICATION_SC_REVIEW,
          };
        return {
          status: TaskStatus.LI_SIGN_PENDING,
          message: ADHOC_FI_SIGN_DONE_SUBMIT_FOR_LI_SIGN_OFF,
          title: TITLE_SIGN_OFF,
          isLiSignEnabled: true,
          notification: NOTIFICATION_LI_SIGN_OFF,
        };
      }
      if (assignedByDEC) {
        return {
          status: TaskStatus.LI_SIGN_PENDING,
          message: INSP_BY_DEC_SUBMIT_FOR_LI_SIGN_OFF,
          title: TITLE_REVIEW,
          isLiSignEnabled: true,
          notification: NOTIFICATION_LI_SIGN_OFF,
        };
      }
      return {
        status: TaskStatus.FI_SIGN_PENDING,
        message: ADHOC_SUBMIT_FOR_FI_SIGN_OFF,
        title: TITLE_SIGN_OFF,
        notification: NOTIFICATION_FI_SIGN_OFF,
      };
    }
  } else {
    if (isLISignAvailable) {
      return {
        status: TaskStatus.SC_PENDING_APPROVAL,
        message: LI_SIGN_DONE_SUBMIT_TO_SC,
        title: TITLE_REVIEW,
        notification: NOTIFICATION_SC_REVIEW,
      };
    }
    if (assignedByDEC) {
      return {
        status: TaskStatus.LI_SIGN_PENDING,
        message: INSP_BY_DEC_SUBMIT_FOR_LI_SIGN_OFF,
        title: TITLE_REVIEW,
        notification: NOTIFICATION_LI_SIGN_OFF,
      };
    }
    return {
      status: TaskStatus.LI_SIGN_PENDING,
      message: ADHOC_SUBMIT_FOR_LI_SIGN_OFF,
      title: TITLE_SIGN_OFF,
      notification: NOTIFICATION_LI_SIGN_OFF,
    };
  }
};

export const hasSpecialCharacter = (name: string) => {
  // eslint-disable-next-line
  return /[@~*`#$%^&\+={}[\]:;"<>,?\/|\\]/.test(name);
};

export const getFormattedDateForNotification = (date: string) => {
  const currDate = new Date();
  const dateIST = new Date(date);
  dateIST.setHours(dateIST.getHours() + 6);
  let milliSeconds = currDate.getTime() - dateIST.getTime();
  const hours = Math.floor(milliSeconds / 1000 / 60 / 60);
  milliSeconds -= hours * 1000 * 60 * 60;
  const minutes = Math.floor(milliSeconds / 1000 / 60);
  milliSeconds -= minutes * 1000 * 60;
  if (hours <= 0) {
    return `${hours < 0 ? 0 : minutes} mins ago`;
  }
  if (hours < 24) {
    return `${hours} hrs ago`;
  }
  return dayjs(date).format('MMM D,YYYY');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const groupBy = (list: any[], key: string) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: { [key: string]: any } = {};
  list.forEach(e => {
    if (e[key]) {
      if (result[String(e[key])]) {
        result[String(e[key])].push(e);
      } else {
        result[String(e[key])] = [e];
      }
    }
  });
  return result;
};

export const handleSortByValueChange = (oldValue: string) => {
  if (oldValue === Filter.NONE) return Filter.ASCENDING;
  if (oldValue === Filter.ASCENDING) return Filter.DESCENDING;
  return Filter.NONE;
};

export const checkValidExtension = (ext: string) => {
  const extensions = [
    '.xlsx',
    '.csv',
    '.xls',
    '.mov',
    '.mp3',
    '.txt',
    '.mp4',
    '.docx',
    '.doc',
    '.pdf',
    '.jpg',
    '.jpeg',
    '.bmp',
    '.gif',
    '.png',
    '.svg',
  ];
  return extensions.some(el => ext.endsWith(el));
};

export const isLiFiScSame = (task: TaskList) =>
  task.fkInspectorField === task.fkInspectorLead &&
  task.fieldInspectorUserId === task.siteCoordinatorUserId;

export const createObjectForBulkReAssignAndSign = (task: TaskList, inspectionStatusId: number) => ({
  taskId: task.pkAssetInspectionTask,
  taskStatusId: task.fkInspectionTaskStatusCategory,
  inspectionId: task.fkAssetInspection,
  fieldInspectorId: task.fkInspectorField,
  leadInspectorId: task.fkInspectorLead,
  inspectionStatusId,
  isLiFiScSame: isLiFiScSame(task),
  clerkId: task.clerkId,
});

const getFileRequestForBulkSignAndStamp = (file: ISignAndStampFileBulk) => ({
  filePath: file.originalPath,
  fileUUID: file.fileUUID,
  taskId: file?.fkAssetInspectionTask || 0,
  inspectionId: Number(file?.inspectionId) || 0,
});

const getVariableForBulkSignAndStamp = (list: ISignAndStampFileBulk[]) => {
  const varialblesList = [];
  let position = 0;
  const BATCH_SIZE = 50;
  while (position < list.length) {
    const batch = list.slice(position, position + BATCH_SIZE);
    varialblesList.push({
      userFileRequest: list[0],
      fileSaveRequest: batch.map(getFileRequestForBulkSignAndStamp),
    });
    position += BATCH_SIZE;
  }
  return varialblesList;
};

export const getBulkSignAndStampPayload = (bulkFilesList: ISignAndStampFileBulk[]) => {
  const bulkFiSign = bulkFilesList.filter(
    file => !file.isLiStamp && file.fileCategory === FileCategory.SIGN,
  );
  const bulkFiStamp = bulkFilesList.filter(
    file => !file.isLiStamp && file.fileCategory === FileCategory.STAMP,
  );
  const bulkLiSign = bulkFilesList.filter(
    file => !!file.isLiStamp && file.fileCategory === FileCategory.SIGN,
  );
  const bulkLiStamp = bulkFilesList.filter(
    file => !!file.isLiStamp && file.fileCategory === FileCategory.STAMP,
  );
  const variables = [];
  if (bulkFiSign.length) {
    const variable = getVariableForBulkSignAndStamp(bulkFiSign);
    variables.push(...variable);
  }
  if (bulkFiStamp.length) {
    const variable = getVariableForBulkSignAndStamp(bulkFiStamp);
    variables.push(...variable);
  }
  if (bulkLiSign.length) {
    const variable = getVariableForBulkSignAndStamp(bulkLiSign);
    variables.push(...variable);
  }
  if (bulkLiStamp.length) {
    const variable = getVariableForBulkSignAndStamp(bulkLiStamp);
    variables.push(...variable);
  }
  return variables;
};

export const getAssetFileType = (label: string) => {
  switch (label) {
    case 'Sub Area Layout':
      return FileType.AreaLayout;
    case 'Asset Drawings':
      return FileType.Drawings;
    case 'Layout':
    case 'Manuals':
      return FileType.Layout;
    case 'Asset Picture':
      return FileType.Pictures;
    default:
      return '';
  }
};

export const removalCategories = {
  1: 'Actioned',
  2: 'Duplicate',
  3: 'Other',
  4: 'Reassessed and Deferred',
};

export const generateActionsList = (deficiency: DeficiencyActionedList): deficiencyAction[] => {
  if (!deficiency) {
    return [];
  }
  const removalCategoriesId = [...Object.keys(removalCategories).map(x => Number(x)), null];
  return [
    {
      actionType: 'Work Order',
      actionId: deficiency.workOrderId,
      actionStatus: deficiency.workOrderStatus,
    },
    {
      actionType: 'Work Request',
      actionId: deficiency.workRequestId,
      actionStatus: deficiency.workRequestStatus,
    },
    {
      actionType: 'Work Package',
      actionId: deficiency.workPackageId,
      actionStatus: deficiency.workPackageStatus,
    },
    { actionType: 'CapMap Project', actionId: deficiency.capMapProjectId, actionStatus: '' },
    { actionType: 'CapMap Task', actionId: deficiency.capMapTaskId, actionStatus: '' },
    {
      actionType: 'Temporary Mitigation',
      actionId:
        deficiency?.componentRemovalCategory &&
        !removalCategoriesId.includes(deficiency.fkComponentRemovalCategory)
          ? deficiency.componentRemovalCategory
          : '',
      actionStatus: '',
    },
    {
      actionType: 'TM Applied Date',
      actionId:
        deficiency.tMAppliedDate &&
        !removalCategoriesId.includes(deficiency.fkComponentRemovalCategory)
          ? dayjs(deficiency.tMAppliedDate).format('DD-MM-YYYY')
          : '',
      actionStatus: '',
    },
    {
      actionType: 'TM Closure Date',
      actionId:
        deficiency.tMClosureDate &&
        !removalCategoriesId.includes(deficiency.fkComponentRemovalCategory)
          ? dayjs(deficiency.tMClosureDate).format('DD-MM-YYYY')
          : '',
      actionStatus: '',
    },
    {
      actionType: 'Comments',
      actionId:
        deficiency.componentRemovalComments &&
        !removalCategoriesId.includes(deficiency.fkComponentRemovalCategory)
          ? deficiency.componentRemovalComments
          : '',
      actionStatus: '',
    },
  ];
};

export const removalCategoryIds = [...Object.keys(removalCategories).map(x => Number(x))];

export const getTagForActiveDeficieny = (
  deficiencyActionedList: DeficiencyActionedList,
  deficiencyComponentRemoval: DeficiencyComponentRemoval,
  deficiency: Deficiency,
) => {
  return (
    deficiencyActionedList &&
    (deficiencyComponentRemoval?.componentRemovalIdBySC &&
    removalCategoryIds.includes(deficiencyComponentRemoval?.componentRemovalIdBySC) &&
    !deficiency.isActive
      ? 'Removed From Planning'
      : deficiency.isReInspectedComponent
      ? 'Re-Inspected'
      : '')
  );
};

export const getFileName = (fileName: string) => {
  const fileNameModified = fileName ? fileName.split('.')[0] : null;
  return fileNameModified;
};

/**
 *
 * @param data
 * @returns role
 */
export const getRoleForMutation = data => {
  const currentUser = JSON.parse(getValueFromLocalStorage('currentUser') || `{}`);

  //  if user is DEC clerk, return DEC
  //  else if user is lead inspector, but should not as field inspector then return LEAD
  //  else return FIELD
  return currentUser.clerkId
    ? InspectorRole.DEC
    : (data?.fkInspectorLead &&
        currentUser.inspectorId === data?.fkInspectorLead &&
        data?.fkInspectorLead !== data?.fkInspectorField) ||
      (data?.leadInspectorId &&
        currentUser.inspectorId === data?.leadInspectorId &&
        data?.leadInspectorId !== data?.fieldInspectorId)
    ? InspectorRole.LEAD
    : InspectorRole.FIELD;
};

export const checkFileCorruption = async (files: File[]): Promise<File[]> => {
  const validFilesList: File[] = [];

  const promises = files.map(file => {
    return new Promise<void>(resolve => {
      const reader = new FileReader();

      reader.onload = e => {
        let content = e.target?.result as string;
        content = content.split(',')[1];

        // Assuming a valid file should have a certain length or specific data pattern
        if (content && content.length >= 1000) {
          validFilesList.push(file);
        }
        resolve();
      };

      reader.onerror = error => {
        console.error(`Error reading ${file.name}:`, error.message);
        resolve();
      };

      if (file.type.startsWith('image/')) {
        reader.readAsDataURL(file); // Reads image files as data URLs
      } else {
        reader.readAsText(file); // Reads other file types as text
      }
    });
  });

  await Promise.all(promises);
  return validFilesList; // Returns the list of valid files
};

/**
 *
 * @param taskData
 * @returns boolean
 */
export const isTaskStatusFIorLISign = taskData => {
  if (
    taskData?.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING ||
    (taskData?.fkInspectorField === taskData?.fkInspectorLead &&
      (taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING ||
        taskData?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL))
  ) {
    return false;
  }
  return true;
};
